import React, { useState } from 'react';
import { ActivityIcon, ServerIcon, AlertTriangleIcon, ClockIcon, BarChart2Icon, RefreshCwIcon, PlusIcon, CheckCircleIcon, Loader2Icon } from 'lucide-react';
import { useSystemHealth, useSystemMetrics, useIncidents, useIncidentOperations } from '../hooks/useSystemHealth.js';
import { getStatusColor, getStatusText } from '../lib/statusUtils.js';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@randompedestrian07/ui';
import { Button, buttonVariants } from '@randompedestrian07/ui';
import { Badge, badgeVariants } from '@randompedestrian07/ui';
import { Progress } from '@randompedestrian07/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@randompedestrian07/ui';
import { Input } from '@randompedestrian07/ui';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@randompedestrian07/ui';

export const SystemHealthPage = () => {
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    title: '',
    severity: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    description: '',
    affectedServices: [] as string[]
  });

  const { data: health, isLoading: healthLoading, refetch: refetchHealth } = useSystemHealth();
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useSystemMetrics();
  const { data: incidents, isLoading: incidentsLoading, refetch: refetchIncidents } = useIncidents();
  const { createIncident, resolveIncident, isLoading: operationsLoading } = useIncidentOperations();

  const handleCreateIncident = async () => {
    await createIncident.mutateAsync(incidentForm);
    setShowIncidentForm(false);
    setIncidentForm({ title: '', severity: 'medium', description: '', affectedServices: [] });
  };

  const handleResolveIncident = async (incidentId: string) => {
    await resolveIncident.mutateAsync(incidentId);
  };

  const calculatePercentage = (value: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center">
          System Health Dashboard
          {healthLoading && <Loader2Icon className="ml-2 animate-spin" size={20} />}
        </h1>
        <div className="flex space-x-3">
          <Button
            onClick={() => {
              refetchHealth();
              refetchMetrics();
              refetchIncidents();
            }}
            disabled={healthLoading || metricsLoading || incidentsLoading}
            variant="secondary"
            className="flex items-center"
          >
            <RefreshCwIcon size={14} className={`mr-1 ${(healthLoading || metricsLoading || incidentsLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowIncidentForm(true)}
            variant="destructive"
            className="flex items-center"
          >
            <PlusIcon size={14} className="mr-1" />
            Create Incident
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader>
            <div className="flex items-center mb-2">
              <ServerIcon className="mr-2 text-blue-400" size={20} />
              <CardTitle>Server Status</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <span className={`h-3 w-3 rounded-full mr-2 ${getStatusColor(health?.status)}`}></span>
              <span className="text-lg font-semibold">{getStatusText(health?.status)}</span>
            </div>
            <p className="text-sm text-muted mt-2">
              {health?.status === 'healthy' ? 'All systems functioning normally' :
                health?.status === 'degraded' ? 'Some services experiencing issues' :
                  health?.status === 'unhealthy' ? 'Major service disruption' :
                    'Status unknown'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center mb-2">
              <ActivityIcon className="mr-2 text-indigo-400" size={20} />
              <CardTitle>Request Volume</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">
              {metrics?.requests?.requestsPerMinute || 0} req/min
            </div>
            <p className="text-sm text-muted mt-2">
              Total: {metrics?.requests?.totalRequests || 0} requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center mb-2">
              <AlertTriangleIcon className="mr-2 text-yellow-400" size={20} />
              <CardTitle>Error Rate</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">
              {(metrics?.requests?.errorRate || 0).toFixed(2)}%
            </div>
            <p className={`text-sm mt-2 ${(metrics?.requests?.errorRate || 0) < 1 ? 'text-green' : 'text-warning'}`}>
              {(metrics?.requests?.errorRate || 0) < 1 ? 'Within normal range' : 'Above normal threshold'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center mb-2">
              <ClockIcon className="mr-2 text-purple-400" size={20} />
              <CardTitle>Avg Response Time</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">
              {metrics?.requests?.averageResponseTime || 0}ms
            </div>
            <p className={`text-sm mt-2 ${(metrics?.requests?.averageResponseTime || 0) < 500 ? 'text-green' : 'text-warning'}`}>
              {(metrics?.requests?.averageResponseTime || 0) < 500 ? 'Fast response times' : 'Slower than usual'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <h2 className="text-xl font-medium flex items-center">
              <BarChart2Icon className="mr-2" size={20} />
              System Performance
            </h2>
          </CardHeader>
          <CardContent>
            <div className="h-64 bg-panel-strong/40 rounded-xl flex items-end p-6 border border-stroke space-x-2">
              {(metrics?.requests?.hourlyDistribution || Array(12).fill(0)).map((count, i) => {
                const maxCount = Math.max(...(metrics?.requests?.hourlyDistribution || [1]));
                const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div className="w-full bg-accent/40 rounded-t-sm transition-all duration-500 hover:bg-accent/60" style={{
                      height: `${Math.max(height, 5)}%`
                    }}></div>
                    <span className="text-[10px] text-muted mt-2 px-1">{i * 5}m</span>
                  </div>
                );
              })}
            </div>
            <div className="text-center text-xs text-muted mt-3">
              Request volume over the last hour (5-minute buckets)
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-xl font-medium">
              Active Incidents
              {incidents?.active && incidents.active.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {incidents.active.length} Active
                </Badge>
              )}
            </h2>
          </CardHeader>
          <CardContent>
            {incidentsLoading ? (
              <div className="flex justify-center items-center h-32">
                <Loader2Icon className="animate-spin" size={24} />
              </div>
            ) : incidents?.active && incidents.active.length > 0 ? (
              <div className="space-y-4">
                {incidents.active.map((incident) => (
                  <div key={incident.id} className="bg-panel rounded-xl p-4 border border-stroke">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-semibold">{incident.title}</h3>
                      <div className="flex items-center space-x-2">
                        <Badge variant={
                          incident.severity === 'critical' ? 'destructive' :
                            incident.severity === 'high' ? 'destructive' :
                              incident.severity === 'medium' ? 'default' : 'secondary'
                        }>
                          {incident.severity}
                        </Badge>
                        <button
                          onClick={() => handleResolveIncident(incident.id)}
                          className="text-green hover:opacity-80 transition-opacity"
                          disabled={operationsLoading}
                        >
                          <CheckCircleIcon size={18} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-muted mb-3">
                      {incident.description}
                    </p>
                    <div className="text-[10px] font-medium uppercase tracking-wider text-muted/50">
                      Started: {new Date(incident.startedAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-8 italic text-sm">
                No active incidents
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-xl font-medium">Resource Utilization</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-panel-strong p-5 rounded-2xl border border-stroke">
              <h3 className="text-sm font-semibold mb-3">CPU Usage</h3>
              <Progress value={metrics?.system?.cpu?.usage || 0} className="mb-3" />
              <div className="flex justify-between text-xs font-medium">
                <span className="text-text">{(metrics?.system?.cpu?.usage || 0).toFixed(1)}% utilized</span>
                <span className="text-muted">{metrics?.system?.cpu?.cores || 0} cores</span>
              </div>
            </div>
            <div className="bg-panel-strong p-5 rounded-2xl border border-stroke">
              <h3 className="text-sm font-semibold mb-3">Memory Usage</h3>
              <Progress value={Number(metrics?.system?.memory?.usagePercent || 0)} className="mb-3" />
              <div className="flex justify-between text-xs font-medium">
                <span className="text-text">{metrics?.system?.memory?.usagePercent || 0}% utilized</span>
                <span className="text-muted">{metrics?.system?.memory?.total || '0MB'} total</span>
              </div>
            </div>
            <div className="bg-panel-strong p-5 rounded-2xl border border-stroke">
              <h3 className="text-sm font-semibold mb-3">Process Memory</h3>
              <Progress
                value={metrics?.process?.memory?.heapUsed && metrics?.process?.memory?.heapTotal
                  ? (Number(metrics.process.memory.heapUsed) / Number(metrics.process.memory.heapTotal) * 100)
                  : 0
                }
                className="mb-3"
              />
              <div className="flex justify-between text-xs font-medium">
                <span className="text-text">{metrics?.process?.memory?.heapUsed || '0MB'} used</span>
                <span className="text-muted">{metrics?.process?.memory?.heapTotal || '0MB'} total</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Incident Creation Modal */}
      <Dialog open={showIncidentForm} onOpenChange={setShowIncidentForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Incident</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <Input
                value={incidentForm.title}
                onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })}
                placeholder="Brief incident title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Severity</label>
              <Select value={incidentForm.severity} onValueChange={(value) => setIncidentForm({ ...incidentForm, severity: value as any })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-muted">Description</label>
              <textarea
                value={incidentForm.description}
                onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
                className="w-full bg-panel-strong border border-stroke rounded-xl px-4 py-3 text-text placeholder-muted focus:outline-none focus:ring-1 focus:ring-accent/20 focus:border-accent/40"
                rows={3}
                placeholder="Describe the incident"
              />
            </div>
            <DialogFooter>
              <div className="flex justify-end space-x-3">
                <Button variant="secondary" onClick={() => setShowIncidentForm(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateIncident}
                  disabled={!incidentForm.title || operationsLoading}
                  variant="destructive"
                >
                  Create
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
